// Confirmation view — replaces the config sections in the right panel after
// a successful submit. Hero stays on the left so the customer's machine is
// still on screen.

import { motion } from "framer-motion";

import { formatDollarsAndCents } from "../format";
import { useConfigurator } from "../store";

// Shared Gammill Sales round-robin booking page. Pre-filled with the
// shopper's details so booking is one click; the utm_* tags let HubSpot
// attribute the meeting to the configurator confirmation page.
const ZOOM_BOOKING_BASE = "https://meetings.hubspot.com/gammill-sales";

function zoomBookingUrl(customer: {
  firstName: string;
  lastName: string;
  email: string;
}): string {
  const params = new URLSearchParams({
    firstName: customer.firstName.trim(),
    lastName: customer.lastName.trim(),
    email: customer.email.trim(),
    utm_source: "configurator",
    utm_medium: "confirmation",
    utm_campaign: "quote_zoom",
  });
  return `${ZOOM_BOOKING_BASE}?${params.toString()}`;
}

export function Confirmation() {
  const { state, dispatch } = useConfigurator();
  const quote = state.generated;
  if (!quote) return null;
  // A rep building a quote for a customer doesn't need to book a Zoom
  // with themselves.
  const showZoom = !state.employee.active;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-start gap-6 py-8"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center bg-amber font-display text-xl font-extrabold text-ink">
          ✓
        </span>
        <span className="font-sans text-[14px] font-bold uppercase tracking-tag text-amber-700">
          Quote · Generated
        </span>
      </div>

      <header className="flex flex-col gap-2">
        <h2 className="font-display text-3xl font-extrabold uppercase leading-tight text-ink">
          Your quote is ready
        </h2>
        <p className="text-steel">
          Quote{" "}
          <span className="font-mono text-ink">{quote.quote_number}</span> for{" "}
          <strong className="text-ink">
            {formatDollarsAndCents(quote.total_cents)}
          </strong>{" "}
          total. Open it below to view it, pay your deposit, or share the link.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={quote.quote_public_url}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-2 rounded-full bg-amber px-8 py-4 font-sans text-base font-bold tracking-[0.02em] text-ink shadow-[0_6px_18px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover"
        >
          Open My Quote →
        </a>
        {showZoom && (
          <a
            href={zoomBookingUrl(state.customer)}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-sans text-base font-bold tracking-[0.02em] text-white transition hover:bg-black"
          >
            Book a 20-minute Zoom walkthrough →
          </a>
        )}
      </div>

      {showZoom ? (
        <p className="border-l-2 border-amber pl-3 font-sans text-sm leading-relaxed text-steel">
          Pick a time and a Gammill product specialist will walk through this
          exact quote with you on Zoom — sizing, options, financing, delivery.
          No time right now? Your Gammill rep will be in touch shortly.
        </p>
      ) : (
        <p className="border-l-2 border-amber pl-3 font-sans text-sm leading-relaxed text-steel">
          Your Gammill rep will be in touch shortly to confirm details.
        </p>
      )}

      <button
        type="button"
        onClick={() => dispatch({ type: "reset" })}
        className="mt-2 font-sans text-[14px] uppercase tracking-tag text-steel underline-offset-4 hover:text-ink hover:underline"
      >
        Configure another machine
      </button>
    </motion.section>
  );
}
